import React from 'react';
import FloatingShapes from "../common/FloatingShapes";

const activities = [
  {
    title: "Nature & Outdoor Exploration",
    description:
      "Connecting children with the natural world through hands-on experiences and outdoor adventures.",
    items: [
      "Nature is My Friend",
      "Waste to Treasure",
      "Kingdom of Plants",
      "Horizon Exploration",
      "Knot Tying Skills",
      "Mountain Biking",
      "Nature Hiking Trips",
      "Mountain Climbing",
      "Living World Exploration",
      "100-Year Life Concept",
      "Nature Observation (Binocular Activities)",
    ],
  },
  {
    title: "Mongolian Culture & Heritage",
    description:
      "Immersing campers in Mongolia's rich traditions, arts, and nomadic heritage.",
    items: [
      "My Homeland",
      "Native Language Enrichment",
      "Traditional Ger (Yurt)",
      "Traditional Games",
      "State Symbols of Mongolia",
      "Morin Khuur (Horsehead Fiddle)",
      "Biyelgee Dance",
      "Archery",
      "Nomadic Lifestyle",
      "Folk Arts",
      "Cultural Heritage Bearers",
    ],
  },
  {
    title: "Creativity & Life Skills Development",
    description:
      "Building practical skills and creative thinking through hands-on workshops.",
    items: [
      "Ceramics",
      "Bead Craft",
      "Knot Art",
      "Wood Craft",
      "Leather Craft",
      "Stone Art",
      "Financial Literacy",
      "Technical Thinking",
      "LEGO Education (STEM Learning)",
    ],
  },
  {
    title: "Personal Development & Social Skills",
    description:
      "Fostering emotional growth, responsibility, and essential life competencies.",
    items: [
      "Memory Development",
      "Emotional Intelligence",
      "Discipline & Responsibility",
      "Health & Reproductive Education",
      "Communication Skills & Attitude Development",
      "Time Management",
      "5S Methodology",
      "Good Habits - Good Child",
      "Law & Everyday Life",
    ],
  },
  {
    title: "Arts, Media & Sports",
    description:
      "Encouraging artistic expression, media literacy, and physical fitness through diverse activities.",
    items: [
      "Film Education",
      "Rhythm & Music",
      "Dance",
      "Nairamdal Media Studio",
      "Kendama",
      "Cricket",
      "Rugby",
      "Volleyball",
      "Basketball",
      "Football",
    ],
  },
];

const sessions = [
  {
    session: "Session 1",
    date: "03 June - 08 June 2026",
    audience: "Grades 1-3",
    note: null,
    international: false,
  },
  {
    session: "Session 2",
    date: "09 June - 16 June 2026",
    audience: "Grades 1-5",
    note: null,
    international: false,
  },
  {
    session: "Session 3",
    date: "17 June - 24 June 2026",
    audience: "Grades 1-5",
    note: null,
    international: false,
  },
  {
    session: "Session 4",
    date: "25 June - 01 July 2026",
    audience: "Grades 5-8",
    note: null,
    international: false,
  },
  {
    session: "Session 5",
    date: "01 July - 10 July 2026",
    audience: "Grades 6-8",
    note: null,
    international: false,
  },
  {
    session: "Session 6",
    date: "15 July - 25 July 2026",
    audience: "Ages 12-17",
    note: '"Camp-100" International Session',
    international: true,
  },
  {
    session: "Session 7",
    date: "25 July - 02 August 2026",
    audience: "Ages 12-17",
    note: '"Mungun Kharaatsai" International Festival',
    international: true,
  },
  {
    session: "Session 8",
    date: "03 August - 13 August 2026",
    audience: "Ages 12-17",
    note: null,
    international: false,
  },
  {
    session: "Session 9",
    date: "14 August - 21 August 2026",
    audience: "Grades 6-11",
    note: null,
    international: false,
  },
  {
    session: "Session 10",
    date: "21 August - 26 August 2026",
    audience: "Grades 6-11",
    note: null,
    international: false,
  },
];

const guidelines = [
  "Participants must be aged 12-17 years.",
  "Participants may join individually or as a group.",
  "All participants must respect camp rules and regulations.",
  "Basic English proficiency is recommended for international sessions.",
  "Participants must be physically and mentally fit to participate in camp activities.",
  "Required documents must be submitted before arrival at the camp.",
  "Travel arrangements to and from the camp are the responsibility of participants.",
];

const activityColors = [
  "border-green-500",
  "border-amber-500",
  "border-purple-500",
  "border-blue-500",
  "border-rose-500",
];

const activityBgColors = [
  "bg-green-50",
  "bg-amber-50",
  "bg-purple-50",
  "bg-blue-50",
  "bg-rose-50",
];

const activityDotColors = [
  "bg-green-400",
  "bg-amber-400",
  "bg-purple-400",
  "bg-blue-400",
  "bg-rose-400",
];

export default function CampPrograms() {
  return (
    <>
      {/* ───────────────── Section 1 – Hero (Dark) ───────────────── */}
      <section className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full bg-secondary-500/15 blur-[120px]" />
          <div className="absolute -bottom-20 right-1/3 w-[400px] h-[400px] rounded-full bg-primary-400/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="default" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative min-h-[60vh] flex items-center pt-40 pb-16 sm:pt-48 sm:pb-20">
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
            {/* Badge pill */}
            <div className="flex justify-center mb-10 animate-fade-in">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-primary-300 backdrop-blur-sm">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary-400 animate-pulse" />
                Camp Programs & Registration
              </div>
            </div>

            {/* Gradient heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 animate-fade-in">
              <span className="bg-gradient-to-r from-primary-400 via-secondary-400 to-primary-300 bg-clip-text text-transparent">
                Camp Programs & Registration
              </span>
            </h1>

            <p className="max-w-3xl mx-auto text-lg sm:text-xl text-slate-300 leading-relaxed animate-fade-in">
              Discover Nairamdal's enriching camp programs designed to inspire
              creativity, build friendships, and create unforgettable memories.
            </p>
          </div>
        </div>
      </section>

      {/* ───────────────── Section 2 – Camp Activities (White) ───────────────── */}
      <div className="relative bg-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-primary-100/60 blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[350px] rounded-full bg-secondary-100/50 blur-[80px]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Section heading */}
          <div className="text-center mb-14 animate-fade-in">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-medium text-primary-700 ring-1 ring-primary-200/50 mb-6">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
              Activities
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">
              Camp Activities
            </h2>
            <div className="mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 mb-6" />
            <p className="text-lg leading-8 text-neutral-600 max-w-3xl mx-auto">
              Our camp offers a diverse range of activities across five key
              areas, each carefully designed to nurture skills, curiosity, and
              personal growth in every camper.
            </p>
          </div>

          {/* Activity cards */}
          <div className="space-y-8">
            {activities.map((activity, index) => (
              <div
                key={activity.title}
                className={`border-l-4 ${activityColors[index]} ${activityBgColors[index]} rounded-2xl p-8 md:p-10 hover:shadow-lg transition-shadow duration-300 animate-slide-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-2">
                  {activity.title}
                </h3>
                <p className="text-neutral-600 mb-5 leading-relaxed">
                  {activity.description}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                  {activity.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-2 text-neutral-700"
                    >
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${activityDotColors[index]} flex-shrink-0`}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ───────────────── Section 3 – Summer Camp Programs 2026 (Dark) ───────────────── */}
      <div className="relative overflow-hidden bg-primary-800">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-secondary-500/15 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-warm-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="alt" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Heading */}
            <div className="text-center mb-14 animate-fade-in">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Summer Camp Programs 2026
              </h2>
              <div className="mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 mb-6" />
              <p className="text-lg leading-8 text-slate-400 max-w-3xl mx-auto">
                In celebration of the 100th anniversary of the Nairamdal
                International Children's Camp, we are proud to present our 2026
                summer camp schedule. Join us for an extraordinary season of
                learning, adventure, and international friendship.
              </p>
            </div>

            {/* Session table */}
            <div className="overflow-x-auto animate-slide-up">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-4 px-4 text-sm font-semibold text-primary-300 uppercase tracking-wider">
                      Session
                    </th>
                    <th className="py-4 px-4 text-sm font-semibold text-primary-300 uppercase tracking-wider">
                      Dates
                    </th>
                    <th className="py-4 px-4 text-sm font-semibold text-primary-300 uppercase tracking-wider">
                      Participants
                    </th>
                    <th className="py-4 px-4 text-sm font-semibold text-primary-300 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {sessions.map((s, index) => (
                    <tr
                      key={s.session}
                      className={`hover:bg-white/5 transition-colors ${
                        s.international ? "bg-secondary-500/10" : ""
                      }`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <td className="py-4 px-4 text-white font-medium whitespace-nowrap">
                        {s.session}
                        {s.international && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-secondary-500/20 border border-secondary-400/30 px-2.5 py-0.5 text-xs font-semibold text-secondary-300">
                            INTERNATIONAL
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-slate-300 whitespace-nowrap">
                        {s.date}
                      </td>
                      <td className="py-4 px-4 text-slate-300">
                        {s.audience}
                      </td>
                      <td className="py-4 px-4 text-slate-400 italic">
                        {s.note || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ───────────────── Section 4 – Registration (White) ───────────────── */}
      <div className="relative bg-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-secondary-100/60 blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[350px] rounded-full bg-primary-100/50 blur-[80px]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-fade-in">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-medium text-primary-700 ring-1 ring-primary-200/50 mb-6">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
              Get in Touch
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">
              Registration & Contact
            </h2>
            <div className="mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" />
          </div>

          <div className="max-w-2xl mx-auto animate-slide-up">
            <div className="bg-neutral-50 border border-neutral-100 rounded-2xl p-8 md:p-10 text-center">
              <h3 className="text-xl font-bold text-neutral-900 mb-6">
                Contact for Registration
              </h3>

              <div className="space-y-4 text-neutral-700">
                <p className="text-lg font-medium">Ms. Mandakhzaya</p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="mailto:mandakhzaya@nairamdal.mn"
                    className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                      />
                    </svg>
                    mandakhzaya@nairamdal.mn
                  </a>
                </div>

                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="w-5 h-5 text-neutral-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                    />
                  </svg>
                  <span className="font-medium">+976 88095254</span>
                  <span className="text-sm text-neutral-500">
                    (Viber, WhatsApp, Telegram)
                  </span>
                </div>

                <div className="pt-4 border-t border-neutral-200">
                  <p className="text-sm text-neutral-500 mb-1">
                    Alternative contact
                  </p>
                  <a
                    href="mailto:icc@nairamdal.mn"
                    className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                      />
                    </svg>
                    icc@nairamdal.mn
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───────────────── Section 5 – Participation Guidelines (Dark) ───────────────── */}
      <div className="relative overflow-hidden bg-primary-800">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-warm-500/15 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="sparse" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14 animate-fade-in">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Participation Guidelines
              </h2>
              <div className="mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" />
            </div>

            <div className="max-w-3xl mx-auto animate-slide-up">
              <div className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10">
                <ul className="space-y-5">
                  {guidelines.map((guideline, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-4 text-slate-300 leading-relaxed"
                    >
                      <span className="flex-shrink-0 mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white text-xs font-bold">
                        {index + 1}
                      </span>
                      {guideline}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
